import React from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid2,
  IconButton,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { Coupon } from "../../../../../models/inventory/Coupon";

interface CouponDetailsPopupProps {
  open: boolean;
  coupon: Coupon | null;
  onClose: () => void;
}

const formatDateTime = (value?: Date | string) => {
  if (!value) return "-";
  const dateValue = typeof value === "string" ? new Date(value) : value;
  return dateValue
    ? dateValue.toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";
};

const getStatusLabel = (coupon: Coupon | null) => {
  if (!coupon) return "-";
  if (coupon.redeemed) return "Redeemed";
  if (coupon.expired) return "Expired";
  if (coupon.reactivated) return "Reactivated";
  if (coupon.opened) return "Opened";
  if (coupon.sent) return "Sent";
  return "Not Sent";
};

const CouponDetailsPopup: React.FC<CouponDetailsPopupProps> = ({
  open,
  coupon,
  onClose,
}) => {
  const theme = useTheme();

  if (!coupon) return null;

  const recipientName = `${
    coupon.user_first_name || coupon.first_name || "-"
  } ${coupon.user_last_name || coupon.last_name || ""}`.trim();

  const summaryCards = [
    { label: "Status", value: getStatusLabel(coupon) },
    { label: "Reminders Sent", value: coupon.reminders_sent ?? "-" },
    {
      label: "Selected Pack",
      value: coupon.pack_names || coupon.variants_in_str || "-",
    },
    { label: "Order ID", value: coupon.order_id || "-" },
  ];

  const infoCards = [
    { label: "Coupon ID", value: coupon.id },
    { label: "Template", value: coupon.template_name || "-" },
    { label: "Customer", value: coupon.customer_name || "-" },
    {
      label: "Recipient",
      content: (
        <Box>
          <CustomTypography variant="heading7">
            {recipientName}
          </CustomTypography>
          <CustomTypography variant="body2" color="text.secondary">
            {coupon.user_email || coupon.email || "-"}
          </CustomTypography>
        </Box>
      ),
    },
    { label: "Created Date", value: formatDateTime(coupon.created_date) },
    { label: "Modified Date", value: formatDateTime(coupon.modified_date) },
  ];

  const timeline = [
    { label: "Sent Date", value: formatDateTime(coupon.sent_date) },
    { label: "Opened Date", value: formatDateTime(coupon.opened_date) },
    { label: "Redeemed Date", value: formatDateTime(coupon.redeemed_date) },
    { label: "Expiry Date", value: formatDateTime(coupon.expiry_date) },
    {
      label: "Reactivated Date",
      value: formatDateTime(coupon.reactivation_date),
    },
    {
      label: "Last Reminder Date",
      value: formatDateTime(coupon.last_reminder_sent_date),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 4,
            boxShadow:
              "0px 30px 60px rgba(15, 23, 42, 0.18), 0px 12px 24px rgba(15, 23, 42, 0.12)",
            overflow: "hidden",
            border: `1px solid ${theme.palette.divider}`,
            background: `linear-gradient(180deg, ${
              theme.palette.customColors?.white || "#FFFFFF"
            } 0%, ${
              theme.palette.customColors?.greyBackground || "#F6F7FB"
            } 100%)`,
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          background: `linear-gradient(120deg, ${
            theme.palette.customColors?.lightPurple || "#E0E7FF"
          } 0%, ${theme.palette.customColors?.white || "#FFFFFF"} 100%)`,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
          <CustomTypography variant="heading4" fontWeight={700}>
            Coupon Details — #{coupon.id}
          </CustomTypography>
          <CustomTypography variant="body2" color="text.secondary">
            Full activity log and metadata for this coupon
          </CustomTypography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent
        sx={{
          px: 3,
          py: 2,
        }}
      >
        <Grid2 container spacing={1} sx={{ mb: 2 }}>
          {summaryCards.map((card) => (
            <Grid2 size={{ xs: 12, sm: 6, md: 3 }} key={card.label}>
              <Box
                sx={{
                  py: 1,
                  px: 2,
                  borderRadius: 2,
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.3,
                }}
              >
                <CustomTypography variant="body2" color="text.secondary">
                  {card.label}
                </CustomTypography>
                <CustomTypography variant="heading6" sx={{ mt: 0.5 }}>
                  {card.value || "-"}
                </CustomTypography>
              </Box>
            </Grid2>
          ))}
        </Grid2>
        <Divider sx={{ my: 2 }} />
        <Grid2 container spacing={1}>
          {infoCards.map((row) => (
            <Grid2 size={{ xs: 12, sm: 6 }} key={row.label}>
              <Box
                sx={{
                  py: 1,
                  px: 2,
                  borderRadius: 2,
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.3,
                }}
              >
                <CustomTypography
                  variant="body2"
                  color={theme.palette.text.secondary}
                >
                  {row.label}
                </CustomTypography>
                {row.content ? (
                  row.content
                ) : (
                  <CustomTypography variant="heading7" sx={{ mt: 0.5 }}>
                    {row.value || "-"}
                  </CustomTypography>
                )}
              </Box>
            </Grid2>
          ))}
        </Grid2>
        <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 1 }}>
          <CustomTypography variant="heading6">Timeline</CustomTypography>
          <Grid2 container spacing={1}>
            {timeline.map((item) => (
              <Grid2 size={{ xs: 12, sm: 6 }} key={item.label}>
                <Box
                  sx={{
                    py: 1,
                    px: 2,
                    borderRadius: 2,
                    backgroundColor: theme.palette.background.paper,
                    border: `1px dashed ${theme.palette.divider}`,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.3,
                  }}
                >
                  <CustomTypography variant="body2" color="text.secondary">
                    {item.label}
                  </CustomTypography>
                  <CustomTypography variant="body1" fontWeight={600}>
                    {item.value}
                  </CustomTypography>
                </Box>
              </Grid2>
            ))}
          </Grid2>
        </Box>
        {coupon.mail_content && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              borderRadius: 2,
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <CustomTypography variant="heading6">
              Email Content
            </CustomTypography>
            <CustomTypography variant="body2" color="text.secondary">
              {coupon.mail_content}
            </CustomTypography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <AppButton variant="grayscale" label="Close" onClick={onClose} />
      </DialogActions>
    </Dialog>
  );
};

export default CouponDetailsPopup;
